<template>
  <FormSection
    v-if="isReady"
    :icon="IconColoredBudget"
    :title="capitalize($t('names.pricing'))">
    <div
      v-if="canShowAuctionType"
      class="form-group row">
      <form-label :is-required="true">{{ capitalize(i('names.auctionType', 1)) }}</form-label>
      <div class="col-sm-6 deliver-on-tabs-slider">
        <tabs-slider
          width="100%"
          height="4rem"
          :selected-tab-i-d="auctionType"
          :is-disabled="!canEditAuctionType"
          :tabs="auctionOptions"
          @selected-tab-changed="(x) => (auctionType = x.id)" />
      </div>
      <span class="col-sm-1 field-help">
        <span v-tooltip="capitalize(i('messages.auctionTypeInfoTooltip'))">
          <icon-info />
        </span>
      </span>
    </div>
    <div class="form-group row">
      <form-label :is-required="true">{{ capitalize($t('names.markupType')) }}</form-label>
      <div class="col-md-8 col-lg-6">
        <tabs-slider
          id="markup_tabs"
          width="100%"
          height="4rem"
          :selected-tab-i-d="selectedFeeType"
          :tabs="feeOptions"
          :show-dividers="true"
          @selected-tab-changed="toggleFeeType" />
      </div>
      <span class="col-sm-1 field-help">
        <span v-tooltip="{ content: getToolTipFormatted() }">
          <icon-info />
        </span>
      </span>
    </div>
    <div class="form-group row">
      <form-label :is-required="true"
        >{{ capitalize($t(selectedFeeType === PackageFeeType.ADVERTISER ? 'messages.advertiserCPM' : 'names.markup')) }}
      </form-label>
      <div class="col-md-8 col-lg-6">
        <formatted-input
          v-if="feeType === PackageFeeType.PERCENT"
          name="markup"
          format="fees"
          input-type="number"
          :integer-only="false"
          :is-money="false"
          :value="feeValue"
          :validation-errors="errors"
          :disabled="false"
          :min="0"
          :max="99.9"
          :step="0.1"
          prefix="%"
          @value-changed="
            (x) => {
              feeValue = x;
            }
          " />
        <formatted-input
          v-else-if="feeType === PackageFeeType.FIXED"
          name="markup"
          input-type="number"
          format="fees"
          :integer-only="false"
          :is-money="true"
          :custom-currency-symbol="deal?.currencyInformation.symbol"
          :value="feeValue"
          :validation-errors="errors"
          :disabled="false"
          :min="0"
          :step="0.1"
          @value-changed="
            (x) => {
              feeValue = x;
            }
          " />

        <formatted-input
          v-else
          name="markup"
          format="0,0.00"
          input-type="number"
          :integer-only="false"
          :is-money="true"
          :value="advertiserCPM"
          :validation-errors="errors"
          :error-name="'cpmFixed'"
          :show-error-message="true"
          :min="0"
          :step="0.1"
          @value-changed="(val) => emits('update:cpm-fixed', val)" />
      </div>
    </div>
  </FormSection>
</template>

<script setup lang="ts">
  import { capitalize, computed, inject, onBeforeMount, Ref, ref, watch } from 'vue';

  import { FormLabel, TabsSlider } from '@/common/components/controls';
  import FormattedInput from '@/common/components/controls/FormattedInput.vue';
  import FormSection from '@/common/components/controls/FormSection.vue';
  import { IconColoredBudget } from '@/common/icons/colored';
  import { IconInfo } from '@/common/icons/monochrome';
  import { Filter, Package } from '@/common/models';
  import AttachableFee from '@/common/models/AttachableFee';
  import EntityFee from '@/common/models/EntityFee';
  import { AuctionType } from '@/common/models/package/AuctionType.enum';
  import PackageFeeType from '@/common/models/package/PackageFeeType.enum';
  import { Pricing } from '@/common/models/package/Pricing.enum';
  import FilterCollection from '@/common/models/query-params/FilterCollection';
  import QueryParams from '@/common/models/query-params/QueryParams';
  import { i, useInjected } from '@/globals';
  import { useDealComputedProperties } from '@/org-app/services/computedProperties/DealComputedProperties';
  import { mapActions, mapOrgAppActions, mapOrgAppState } from '@/utils/StateMapping';
  import MessageCollection from '@/validator/MessageCollection';
  import i18n from '@/vueI18n';

  interface IEditDealSustainabilityProps {
    isCopiedDeal: boolean;
    canShowAuctionType: boolean;
    canEditAuctionType: boolean;
  }

  const { isCopiedDeal, canShowAuctionType, canEditAuctionType } = defineProps<IEditDealSustainabilityProps>();

  const deal: Ref<Package> = useInjected<Package>('deal');
  const errors: Ref<MessageCollection> = inject('errors', null);

  const { cpmFixed } = useDealComputedProperties(deal);

  const feeOptions = [
    { id: PackageFeeType.FIXED, name: capitalize(i18n.global.t('messages.fixedCPM', { n: 1 })) },
    { id: PackageFeeType.PERCENT, name: capitalize(i18n.global.t('names.percentage')) },
    { id: PackageFeeType.ADVERTISER, name: capitalize(i18n.global.t('messages.advertiserCPM')) },
  ];

  const auctionOptions = [
    { id: AuctionType.FirstPrice, name: capitalize(i18n.global.t('states.firstPrice')) },
    { id: AuctionType.FixedPrice, name: capitalize(i18n.global.t('states.fixedPrice')) },
  ];

  const { organization } = mapOrgAppState();
  const { getAttachableFees } = mapOrgAppActions();
  const { updatePackageEntityFees } = mapActions();

  const emits = defineEmits(['update:cpm-fixed', 'update:pricing-type']);

  const advertiserCPM = ref(cpmFixed.value ?? 0);
  const attachableFeeCategoryID = ref(null);
  const isReady = ref(false);

  const { auctionType } = useDealComputedProperties(deal);

  const feeValue = computed({
    get: () => {
      if (!feeType.value) {
        return cpmFixed.value;
      }
      return feeType.value === PackageFeeType.PERCENT
        ? parseFloat((deal.value.feeValue * 100).toFixed(12))
        : deal.value.feeValue;
    },
    set: (val) => {
      const formattedVal = parseFloat(val.toFixed(12));
      const fee = [
        EntityFee.fromObject({
          value: feeType.value === PackageFeeType.FIXED ? formattedVal : formattedVal / 100,
          feeType: feeType.value,
          attachableFeeCategoryID: deal.value.feeAttachableCategoryID ?? attachableFeeCategoryID.value,
        }),
      ];
      updatePackageEntityFees({
        package: deal.value,
        value: feeType.value === PackageFeeType.ADVERTISER ? null : fee,
      });
    },
  });

  const feeType = computed<PackageFeeType>({
    get: () => {
      return (deal.value?.feeType as PackageFeeType) ?? PackageFeeType.ADVERTISER;
    },
    set: (val) => {
      const fee = [
        EntityFee.fromObject({
          value: 0,
          feeType: val,
          attachableFeeCategoryID: deal.value.feeAttachableCategoryID ?? attachableFeeCategoryID.value,
        }),
      ];
      updatePackageEntityFees({
        package: deal.value,
        value: val === PackageFeeType.ADVERTISER ? null : fee,
      });
      emits('update:pricing-type', val === PackageFeeType.ADVERTISER ? Pricing.Static : Pricing.RateCard);
    },
  });

  const selectedFeeType = computed(() => {
    return feeType.value ?? PackageFeeType.ADVERTISER;
  });

  function toggleFeeType(mode: (typeof feeOptions)[number]) {
    feeType.value = mode.id;
  }

  function getToolTipFormatted() {
    const optionInfo = (header: string, description: string) =>
      `
      <h4 style="text-decoration: underline; font-weight: bolder; margin-bottom: 1rem;">${header}</h4>
      <ul><li style="font-size: small;">${description}</li></ul>
      `;
    return `<div style="text-align: left;">
      ${optionInfo(
        capitalize(i18n.global.t('messages.fixedCPM')),
        capitalize(i18n.global.t('messages.fixedCPMDescription')),
      )}
      ${optionInfo(
        capitalize(i18n.global.t('names.percentage')),
        capitalize(i18n.global.t('messages.percentageDescription')),
      )}
      ${optionInfo(
        capitalize(i18n.global.t('messages.advertiserCPM')),
        capitalize(i18n.global.t('messages.advertiserCPMDescription')),
      )}
    </div>`;
  }

  async function loadAttachableFees() {
    const filterCollection = new FilterCollection([
      new Filter('organization_id', [organization.id]),
      new Filter('fee_category.name', ['CURATOR_MARKUP']),
    ]);
    const params = QueryParams.for({
      filterCollection,
      include: ['attachable_fee_category_id'],
    });
    const attachableFees: AttachableFee[] = await getAttachableFees({ params });
    attachableFeeCategoryID.value = attachableFees.length ? attachableFees[0].attachableFeeCategoryID : null;
  }

  onBeforeMount(async () => {
    await loadAttachableFees();
    const hasAdvertiserCPM =
      !deal.value.entityFees?.length || (deal.value.entityFees[0]?.value === 0 && cpmFixed.value > 0);
    if (deal.value.isNew && !isCopiedDeal) {
      feeType.value = PackageFeeType.FIXED;
    } else if (hasAdvertiserCPM) {
      feeType.value = PackageFeeType.ADVERTISER;
    }
    isReady.value = true;
  });

  // To ensure that the advertiserCPM is reset when the feeType is changed from ADVERTISER to another type
  watch(feeType, (newType, prevType) => {
    if (!prevType || prevType === newType) return;
    if (prevType === PackageFeeType.ADVERTISER) {
      advertiserCPM.value = null;
    }
    // Restore the previous fixed CPM or revert to 0 if the new type is advertiser CPM
    if (newType === PackageFeeType.ADVERTISER) {
      advertiserCPM.value = cpmFixed.value || 0;
    }
  });

  watch(advertiserCPM, (newCPM, oldCPM) => {
    if (newCPM === oldCPM) return;
    emits('update:cpm-fixed', newCPM);
  });
</script>
